import { useState, type FormEvent } from 'react';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Honeypot check
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      return;
    }

    // Remove honeypot from payload
    formData.delete('website_alt');

    setStatus('sending');
    setErrorMsg('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch('https://readdy.ai/api/form/d9qoh9me3oq4jqumto0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMsg(
          serverMsg && typeof serverMsg === 'string' && serverMsg.includes('spam')
            ? '提交失败，请稍后再试。'
            : serverMsg || '提交失败，请稍后重试。'
        );
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请检查网络连接后重试。');
    }
  };

  return (
    <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 md:p-8">
      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
        发送消息
      </h3>

      {status === 'success' ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-500/10 mb-4">
            <i className="ri-check-line text-3xl text-accent-500"></i>
          </div>
          <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-2">消息已发送</h4>
          <p className="text-sm text-foreground-500 mb-4">感谢您的留言，我们会在24小时内回复。</p>
          <button
            onClick={() => setStatus('idle')}
            className="text-sm text-accent-500 hover:text-accent-400 font-medium transition-colors"
          >
            发送新消息
          </button>
        </div>
      ) : (
        <form
          data-readdy-form=""
          onSubmit={handleSubmit}
          className="space-y-5"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                姓名
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors"
                placeholder="请输入您的姓名"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                邮箱
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors"
                placeholder="请输入您的邮箱"
              />
            </div>
          </div>
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
              主题
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              required
              className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors"
              placeholder="请选择或输入主题"
            />
          </div>
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
              消息内容
            </label>
            <textarea
              id="message"
              name="message"
              rows={5}
              maxLength={500}
              required
              className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors resize-none"
              placeholder="请输入您的消息内容（不超过500字符）"
            ></textarea>
            <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
          </div>

          {/* Honeypot */}
          <div className="hp-field-wrap" aria-hidden="true">
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              readOnly
              aria-hidden="true"
            />
          </div>

          {status === 'error' && errorMsg && (
            <div className="p-3 bg-primary-100/50 border border-primary-200/50 rounded-lg">
              <p className="text-sm text-primary-700">{errorMsg}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'sending'}
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-background-950 font-medium rounded-lg hover:bg-accent-400 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === 'sending' ? '发送中...' : '发送消息'}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-send-plane-line"></i>
            </div>
          </button>
        </form>
      )}
    </div>
  );
}